import { NestedValue, UnpackNestedValue } from 'react-hook-form'
import { isNil } from 'lodash'
import {
  AnyProviderParametersInput,
  DemandFragment,
  DhwHeatingMode,
  FuelType,
  HeatCircuit,
  HeatingOrCoolingSource,
  ProviderParametersFragment,
  ProviderType,
} from '../../../generated/graphql'
import { parseDecimal, parseKwToW } from '../../../util/parsers'
import { formatEnergyCapacity, formatPower } from '../../../util/formatters'
import { getSortedHeatingDemands } from '../../../demands/demand-util'

export type FormValuesParameters = {
  inefficientDevice?: {
    efficiency?: string
  }
  heatBoiler?: {
    efficiency?: string
    fuelType?: FuelType
  }
  heatingAirHeatPump?: {
    cop?: string
    maxPower?: string
    minOutdoorTemperature?: string
  }
  heatingGroundHeatPump?: {
    cop?: string
    maxPower?: string
  }
  coolingAirHeatPump?: {
    eer?: string
    maxPower?: string
    freeCooling?: boolean
    freeCoolingMaxOutdoorTemperature?: string
    freeCoolingEer?: string
    freeCoolingMaxPower?: string
  }
  coolingGroundHeatPump?: {
    eer?: string
    maxPower?: string
    freeCooling?: boolean
    freeCoolingEer?: string
    freeCoolingMaxPower?: string
  }
  waterCoolingHeatPump?: {
    eer?: string
    freeCooling?: boolean
    freeCoolingMaxOutdoorTemperature?: string
    freeCoolingEer?: string
  }
  circulationHeatPump?: {
    cop?: string
    maxPower?: string
  }
  hybridHeatPump?: {
    performanceTableId?: string
    copModifier?: string
    heatSourceAvailable?: boolean
    heatExhaustAvailable?: boolean
    heatRecycleAvailable?: boolean
    heatingPower?: string
    heatingTemperatureCurveId?: string
    dhwHeatingMode?: DhwHeatingMode
    heatCircuits?: HeatCircuit[]
    coolingTemperatureCurveId?: string
    baseHeatSource?: HeatingOrCoolingSource
    groundHeatSourceTemperature?: string
    groundHeatSourceMaxPower?: string
    airHeatSourceMinTemperature?: string
    wasteHeatSourceAHourSeriesId?: string
    wasteHeatSourceATemperature?: string
    wasteHeatSourceBHourSeriesId?: string
    wasteHeatSourceBTemperature?: string
    freeCoolingSource?: HeatingOrCoolingSource
    freeGroundCoolingMaxPower?: string
    freeAirCoolingMaxTemperature?: string
    freeCoolingEer?: string
    coolingSource?: HeatingOrCoolingSource
    groundExhaustTemperature?: string
  }
  preheater?: {
    relativePower?: string
  }
  solarPanel?: {
    area?: string
    solarEfficiency?: string
    ratingEfficiency?: string
    isWasteSold?: boolean
  }
  battery?: {
    capacity?: string
    chargePower?: string
    dischargePower?: string
  }
}

export type InternalFormValues = {
  name?: string
  type?: ProviderType
  parameters?: FormValuesParameters
  hourSeriesIds?: NestedValue<string[]>
  performanceTableIds?: NestedValue<string[]>
  temperatureCurveIds?: NestedValue<string[]>
}

export type FormValues = UnpackNestedValue<InternalFormValues>

export const convertFormParametersToInputParameters = (
  parameters?: FormValuesParameters,
): AnyProviderParametersInput => {
  if (!parameters) {
    return {}
  }

  const inputParameters = {
    inefficientDevice: {
      efficiency: parseDecimal(parameters.inefficientDevice?.efficiency),
    },

    heatBoiler: {
      efficiency: parseDecimal(parameters.heatBoiler?.efficiency),
      fuelType: parameters.heatBoiler?.fuelType,
    },

    heatingAirHeatPump: {
      cop: parseDecimal(parameters.heatingAirHeatPump?.cop),
      maxPower: parseKwToW(parameters.heatingAirHeatPump?.maxPower),
      minOutdoorTemperature: parseDecimal(
        parameters.heatingAirHeatPump?.minOutdoorTemperature,
      ),
    },

    heatingGroundHeatPump: {
      cop: parseDecimal(parameters.heatingGroundHeatPump?.cop),
      maxPower: parseKwToW(parameters.heatingGroundHeatPump?.maxPower),
    },

    coolingAirHeatPump: {
      eer: parseDecimal(parameters.coolingAirHeatPump?.eer),
      maxPower: parseKwToW(parameters.coolingAirHeatPump?.maxPower),
      freeCooling: parameters.coolingAirHeatPump?.freeCooling,
      freeCoolingMaxOutdoorTemperature: parseDecimal(
        parameters.coolingAirHeatPump?.freeCoolingMaxOutdoorTemperature,
      ),
      freeCoolingEer: parseDecimal(
        parameters.coolingAirHeatPump?.freeCoolingEer,
      ),
      freeCoolingMaxPower: parseKwToW(
        parameters.coolingAirHeatPump?.freeCoolingMaxPower,
      ),
    },

    coolingGroundHeatPump: {
      eer: parseDecimal(parameters.coolingGroundHeatPump?.eer),
      maxPower: parseKwToW(parameters.coolingGroundHeatPump?.maxPower),
      freeCooling: parameters.coolingGroundHeatPump?.freeCooling,
      freeCoolingEer: parseDecimal(
        parameters.coolingGroundHeatPump?.freeCoolingEer,
      ),
      freeCoolingMaxPower: parseKwToW(
        parameters.coolingGroundHeatPump?.freeCoolingMaxPower,
      ),
    },

    waterCoolingHeatPump: {
      eer: parseDecimal(parameters.waterCoolingHeatPump?.eer),
      freeCooling: parameters.waterCoolingHeatPump?.freeCooling,
      freeCoolingMaxOutdoorTemperature: parseDecimal(
        parameters.waterCoolingHeatPump?.freeCoolingMaxOutdoorTemperature,
      ),
      freeCoolingEer: parseDecimal(
        parameters.waterCoolingHeatPump?.freeCoolingEer,
      ),
    },

    circulationHeatPump: {
      cop: parseDecimal(parameters.circulationHeatPump?.cop),
      maxPower: parseKwToW(parameters.circulationHeatPump?.maxPower),
    },

    hybridHeatPump: {
      performanceTableId: parameters.hybridHeatPump?.performanceTableId,
      copModifier: parseDecimal(parameters.hybridHeatPump?.copModifier),
      heatSourceAvailable: parameters.hybridHeatPump?.heatSourceAvailable,
      heatExhaustAvailable: parameters.hybridHeatPump?.heatExhaustAvailable,
      heatRecycleAvailable: parameters.hybridHeatPump?.heatRecycleAvailable,
      heatingPower: parseKwToW(parameters.hybridHeatPump?.heatingPower),
      heatingTemperatureCurveId:
        parameters.hybridHeatPump?.heatingTemperatureCurveId,
      dhwHeatingMode: parameters.hybridHeatPump?.dhwHeatingMode,
      heatCircuits: parameters.hybridHeatPump?.heatCircuits,
      coolingTemperatureCurveId:
        parameters.hybridHeatPump?.coolingTemperatureCurveId,
      baseHeatSource: parameters.hybridHeatPump?.baseHeatSource,
      groundHeatSourceTemperature: parseDecimal(
        parameters.hybridHeatPump?.groundHeatSourceTemperature,
      ),
      groundHeatSourceMaxPower: parseKwToW(
        parameters.hybridHeatPump?.groundHeatSourceMaxPower,
      ),
      airHeatSourceMinTemperature: parseDecimal(
        parameters.hybridHeatPump?.airHeatSourceMinTemperature,
      ),
      wasteHeatSourceAHourSeriesId:
        parameters.hybridHeatPump?.wasteHeatSourceAHourSeriesId,
      wasteHeatSourceATemperature: parseDecimal(
        parameters.hybridHeatPump?.wasteHeatSourceATemperature,
      ),
      wasteHeatSourceBHourSeriesId:
        parameters.hybridHeatPump?.wasteHeatSourceBHourSeriesId,
      wasteHeatSourceBTemperature: parseDecimal(
        parameters.hybridHeatPump?.wasteHeatSourceBTemperature,
      ),
      freeCoolingSource: parameters.hybridHeatPump?.freeCoolingSource,
      freeGroundCoolingMaxPower: parseKwToW(
        parameters.hybridHeatPump?.freeGroundCoolingMaxPower,
      ),
      freeAirCoolingMaxTemperature: parseDecimal(
        parameters.hybridHeatPump?.freeAirCoolingMaxTemperature,
      ),
      freeCoolingEer: parseDecimal(parameters.hybridHeatPump?.freeCoolingEer),
      coolingSource: parameters.hybridHeatPump?.coolingSource,
      groundExhaustTemperature: parseDecimal(
        parameters.hybridHeatPump?.groundExhaustTemperature,
      ),
    },

    preheater: {
      relativePower: parseDecimal(parameters.preheater?.relativePower),
    },

    solarPanel: {
      area: parseDecimal(parameters.solarPanel?.area),
      solarEfficiency: parseDecimal(parameters.solarPanel?.solarEfficiency),
      ratingEfficiency: parseDecimal(parameters.solarPanel?.ratingEfficiency),
      isWasteSold: parameters.solarPanel?.isWasteSold,
    },

    battery: {
      capacity: parseKwToW(parameters.battery?.capacity),
      chargePower: parseKwToW(parameters.battery?.chargePower),
      dischargePower: parseKwToW(parameters.battery?.dischargePower),
    },
  }

  const filterDeviceParameters = (
    deviceParameters?: Record<string, unknown>,
  ) => {
    if (!deviceParameters) {
      return {}
    }

    return Object.fromEntries(
      Object.entries(deviceParameters).filter(
        ([_, parameterValue]) => parameterValue !== undefined,
      ),
    )
  }

  return Object.fromEntries(
    Object.entries(inputParameters)
      .map(([deviceKey, deviceParameters]) => [
        deviceKey,
        filterDeviceParameters(deviceParameters),
      ])
      .filter(
        ([_, deviceParameters]) => Object.keys(deviceParameters).length > 0,
      ),
  ) as AnyProviderParametersInput
}

export const convertProviderParametersToFormParameters = (
  parameters: ProviderParametersFragment | undefined,
  demands: DemandFragment[],
): FormValuesParameters => {
  if (!parameters) {
    return {}
  }

  const mapHeatCircuitsWithDemands = (
    heatCircuits: HeatCircuit[],
  ): HeatCircuit[] =>
    getSortedHeatingDemands(demands).map((demand) => {
      const circuit = heatCircuits.find((hc) => hc.demandId === demand.id)
      return circuit
        ? { ...circuit }
        : {
            __typename: 'HeatCircuit' as const,
            demandId: demand.id,
            temperatureCurveId: '',
            isDhwCircuit: false,
          }
    })

  return {
    inefficientDevice:
      parameters.__typename === 'InefficientParameters'
        ? { efficiency: String(parameters.efficiency) }
        : undefined,

    heatBoiler:
      parameters.__typename === 'HeatBoilerParameters'
        ? {
            efficiency: String(parameters.efficiency),
            fuelType: parameters.fuelType,
          }
        : undefined,

    heatingAirHeatPump:
      parameters.__typename === 'HeatingAirHeatPumpParameters'
        ? {
            cop: String(parameters.cop),
            maxPower: formatPower(parameters.maxPower, { omitUnit: true }),
            minOutdoorTemperature: String(parameters.minOutdoorTemperature),
          }
        : undefined,

    heatingGroundHeatPump:
      parameters.__typename === 'HeatingGroundHeatPumpParameters'
        ? {
            cop: String(parameters.cop),
            maxPower: formatPower(parameters.maxPower, { omitUnit: true }),
          }
        : undefined,

    coolingAirHeatPump:
      parameters.__typename === 'CoolingAirHeatPumpParameters'
        ? {
            eer: String(parameters.eer),
            maxPower: formatPower(parameters.maxPower, { omitUnit: true }),
            freeCooling: parameters.freeCooling,
            freeCoolingMaxOutdoorTemperature: String(
              parameters.freeCoolingMaxOutdoorTemperature,
            ),
            freeCoolingEer: String(parameters.freeCoolingEer),
            freeCoolingMaxPower: formatPower(parameters.freeCoolingMaxPower, {
              omitUnit: true,
            }),
          }
        : undefined,

    coolingGroundHeatPump:
      parameters.__typename === 'CoolingGroundHeatPumpParameters'
        ? {
            eer: String(parameters.eer),
            maxPower: formatPower(parameters.maxPower, { omitUnit: true }),
            freeCooling: parameters.freeCooling,
            freeCoolingEer: String(parameters.freeCoolingEer),
            freeCoolingMaxPower: formatPower(parameters.freeCoolingMaxPower, {
              omitUnit: true,
            }),
          }
        : undefined,

    waterCoolingHeatPump:
      parameters.__typename === 'WaterCoolingHeatPumpParameters'
        ? {
            eer: String(parameters.eer),
            freeCooling: parameters.freeCooling,
            freeCoolingMaxOutdoorTemperature: String(
              parameters.freeCoolingMaxOutdoorTemperature,
            ),
            freeCoolingEer: String(parameters.freeCoolingEer),
          }
        : undefined,

    circulationHeatPump:
      parameters.__typename === 'CirculationHeatPumpParameters'
        ? {
            cop: String(parameters.cop),
            maxPower: formatPower(parameters.maxPower, { omitUnit: true }),
          }
        : undefined,

    hybridHeatPump:
      parameters.__typename === 'HybridHeatPumpParameters'
        ? {
            performanceTableId: parameters.performanceTableId,
            copModifier: String(parameters.copModifier),
            heatSourceAvailable: parameters.heatSourceAvailable,
            heatExhaustAvailable: parameters.heatExhaustAvailable,
            heatRecycleAvailable: parameters.heatRecycleAvailable,
            heatingPower: formatPower(parameters.heatingPower, {
              omitUnit: true,
            }),
            heatingTemperatureCurveId:
              parameters.heatingTemperatureCurveId ?? undefined,
            dhwHeatingMode: parameters.dhwHeatingMode ?? undefined,
            heatCircuits: mapHeatCircuitsWithDemands(
              parameters.heatCircuits ?? [],
            ),
            coolingTemperatureCurveId:
              parameters.coolingTemperatureCurveId ?? undefined,
            baseHeatSource: parameters.baseHeatSource ?? undefined,
            groundHeatSourceTemperature: !isNil(
              parameters.groundHeatSourceTemperature,
            )
              ? String(parameters.groundHeatSourceTemperature)
              : undefined,
            groundHeatSourceMaxPower: formatPower(
              parameters.groundHeatSourceMaxPower,
              { omitUnit: true },
            ),
            airHeatSourceMinTemperature: !isNil(
              parameters.airHeatSourceMinTemperature,
            )
              ? String(parameters.airHeatSourceMinTemperature)
              : undefined,
            wasteHeatSourceAHourSeriesId:
              parameters.wasteHeatSourceAHourSeriesId ?? undefined,
            wasteHeatSourceATemperature: !isNil(
              parameters.wasteHeatSourceATemperature,
            )
              ? String(parameters.wasteHeatSourceATemperature)
              : undefined,
            wasteHeatSourceBHourSeriesId:
              parameters.wasteHeatSourceBHourSeriesId ?? undefined,
            wasteHeatSourceBTemperature: !isNil(
              parameters.wasteHeatSourceBTemperature,
            )
              ? String(parameters.wasteHeatSourceBTemperature)
              : undefined,
            freeCoolingSource: parameters.freeCoolingSource ?? undefined,
            freeGroundCoolingMaxPower: formatPower(
              parameters.freeGroundCoolingMaxPower,
              { omitUnit: true },
            ),
            freeAirCoolingMaxTemperature: !isNil(
              parameters.freeAirCoolingMaxTemperature,
            )
              ? String(parameters.freeAirCoolingMaxTemperature)
              : undefined,
            freeCoolingEer: !isNil(parameters.freeCoolingEer)
              ? String(parameters.freeCoolingEer)
              : undefined,
            coolingSource: parameters.coolingSource ?? undefined,
            groundExhaustTemperature: !isNil(
              parameters.groundExhaustTemperature,
            )
              ? String(parameters.groundExhaustTemperature)
              : undefined,
          }
        : undefined,

    preheater:
      parameters.__typename === 'PreheaterParameters'
        ? {
            relativePower: String(parameters.relativePower),
          }
        : undefined,

    solarPanel:
      parameters.__typename === 'SolarPanelParameters'
        ? {
            area: String(parameters.area),
            solarEfficiency: String(parameters.solarEfficiency),
            ratingEfficiency: String(parameters.ratingEfficiency),
            isWasteSold: Boolean(parameters.isWasteSold),
          }
        : undefined,

    battery:
      parameters.__typename === 'BatteryParameters'
        ? {
            capacity: formatEnergyCapacity(parameters.capacity, {
              omitUnit: true,
            }),
            chargePower: formatPower(parameters.chargePower, {
              omitUnit: true,
            }),
            dischargePower: formatPower(parameters.dischargePower, {
              omitUnit: true,
            }),
          }
        : undefined,
  }
}
